/**
 * KpiCard — the standard "big number + label + trend" card on dashboards.
 *
 * The number renders with the Aurora gradient text class; the trend pill
 * uses semantic colors. Pass `icon` for the small badge in the corner.
 */
import { type ReactNode } from "react";
import { cn } from "@/lib/cn";

export type KpiTrend = "up" | "down" | "flat";

export interface KpiCardProps {
  label: string;
  value: ReactNode;
  /** Trend descriptor like "+18 vs 24h" or "—". */
  trendLabel?: string;
  trend?: KpiTrend;
  icon?: ReactNode;
  className?: string;
}

const trendColors: Record<KpiTrend, string> = {
  up: "text-mint",
  down: "text-rose",
  flat: "text-fg-mute",
};
const trendArrows: Record<KpiTrend, string> = {
  up: "▲",
  down: "▼",
  flat: "—",
};

export function KpiCard({
  label,
  value,
  trend,
  trendLabel,
  icon,
  className,
}: KpiCardProps) {
  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-2xl border border-line bg-surface px-5 py-4",
        "shadow-[0_1px_0_rgba(11,15,35,0.04),0_12px_40px_-20px_rgba(125,108,255,0.25)]",
        className,
      )}
    >
      {/* Soft violet tint in the top-left corner — the Aurora signature. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_100%_at_0%_0%,rgba(125,108,255,0.12),transparent_50%)]"
      />
      <div className="relative flex items-center justify-between text-xs font-medium text-fg-dim">
        <span>{label}</span>
        {icon ? (
          <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-aurora-soft text-violet">
            {icon}
          </span>
        ) : null}
      </div>
      <div className="relative mt-2.5 mb-1 text-[2rem] font-extrabold leading-none tracking-tight text-aurora">
        {value}
      </div>
      {trend && trendLabel ? (
        <div className={cn("relative font-mono text-[11px]", trendColors[trend])}>
          {trendArrows[trend]} {trendLabel}
        </div>
      ) : null}
    </div>
  );
}
